import { CallcenterWizardData, Sparvertrag } from '@/types/callcenter';
import { Customer, PartnerData, Child } from '@/types/customer';

/**
 * Single Source der Rueckrichtung: Customer (aus der API) →
 * Partial<CallcenterWizardData>. Gegenstueck zu
 * `buildWizardCustomerPayload` (Hinrichtung Wizard → Customer-Payload).
 *
 * Wird beim Laden eines Bestandskontakts in den Wizard verwendet
 * (`searchCustomer`). Vorher gab es ein hand-gepflegtes, gedriftetes
 * Mapping direkt in `searchCustomer`; pro Cluster (B/C/2/3/4) musste
 * eigentlich auch hier nachgezogen werden — was nicht passiert ist.
 * Diagnose 2026-06-10.
 *
 * Wichtig: Partner-Quelle ist `customer.partner_data` (JSONB,
 * Decision #69), nicht die Eloquent-Relation `customer.partner`
 * — die ist fuer Wizard-Kunden grundsaetzlich null.
 *
 * Verlustpunkt: `pays_church_tax` (Boolean) → 'ja' verliert die
 * Wizard-Unterscheidung 'ja_austreten'; aus dem Customer-Bool ist sie
 * nicht rekonstruierbar.
 */

const MARITAL_EN_TO_WIZARD: Record<string, string> = {
  single: 'single',
  married: 'verheiratet',
  divorced: 'single',
  widowed: 'verwitwet',
  cohabiting: 'paar',
  single_parent: 'alleinerziehend',
};

// Umkehr von CustomerFieldMapper::ENUM_VALUE_MAP['occupation'] (BE).
// Mehrfach-Slugs werden auf den Default-Wizard-Label zurueckgemappt:
// - ss_contributions → 'Angestellt' (Wizard hat zudem 'Auszubildender' +
//   'Elternzeit', aber aus dem Customer-Slug ist sie nicht
//   rekonstruierbar; verlustbehaftet, dokumentiert).
// - self_emp_no_emp → 'Selbstständig' (statt 'Freiberufler').
// - job_seeker → 'Arbeitssuchend' (statt 'Student').
// Slugs ohne Wizard-Pendant (emp_md_partner, mini_job, disabled,
// public_sector_employee) bleiben als '' und werden im Wizard nicht
// vorausgewaehlt.
const EMPLOYMENT_EN_TO_LABEL: Record<string, string> = {
  ss_contributions: 'Angestellt',
  self_emp_no_emp: 'Selbstständig',
  self_emp_with_emp: 'Inhaber Firma',
  civil_servant: 'Beamter',
  job_seeker: 'Arbeitssuchend',
  housewife_husband: 'Hausfrau/Hausmann',
  old_age_pensioner: 'Rentner',
};

const toNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const yearOf = (date: string | null | undefined): number | null => {
  if (!date) return null;
  const y = new Date(date).getFullYear();
  return Number.isFinite(y) ? y : null;
};

const boolToJaNein = (v: unknown): string => (v === true ? 'Ja' : 'Nein');
const boolToJaNeinSmall = (v: unknown): string => (v === true ? 'ja' : 'nein');
const boolToJaNeinOderLeer = (v: unknown, ja: string, nein: string): string =>
  v === true ? ja : v === false ? nein : '';

/**
 * Mapping fuer den Mandanten-Teil. Werte, die im Customer nicht vorhanden
 * sind, kommen mit ihrem Wizard-Default zurueck (siehe initialWizardData),
 * damit das Spread im Store kein Feld auf undefined setzt.
 */
function mapMandant(c: Partial<Customer>): Partial<CallcenterWizardData> {
  const childrenArr: Child[] = Array.isArray(c.children) ? c.children : (c.children ? [c.children as Child] : []);

  return {
    haushaltssituation: MARITAL_EN_TO_WIZARD[c.marital_status as string] ?? '',
    berufsstand1: EMPLOYMENT_EN_TO_LABEL[c.employment_status as string] ?? '',
    berufsbezeichnung: c.job_title ?? '',
    geburtsjahr: yearOf(c.date_of_birth),
    bruttogehalt1: toNum(c.gross_salary),
    nettogehalt1: toNum(c.net_salary),
    einkuenfte: c.additional_income_type ?? 'keine',
    einkuenfteHoehe: toNum(c.additional_income_amount),
    kinder: (c.child_benefit_eligible_children ?? 0) + (c.child_benefit_non_eligible_children ?? 0),
    kinderImHaushalt: c.child_benefit_eligible_children ?? 0,
    kinderGeburtsjahre: childrenArr
      .map((ch) => (ch.birthdate ? new Date(ch.birthdate).getFullYear() : 0))
      .filter((y): y is number => Boolean(y)),
    plz: c.postal_code ?? '',
    ort: c.city ?? '',
    // Cluster B (Wizard-Schreibpfad): salutation, additional_email; lead_source
    // und device. Heute beim Laden noch leer.
    anrede: (c as Record<string, unknown>).salutation as string ?? '',
    leadherkunft: (c as Record<string, unknown>).lead_source as string ?? '',
    device: (c as Record<string, unknown>).device as string ?? '',
    // Cluster 2: Wizard-Freitext-Notiz.
    notizen: c.wizard_notes ?? '',
    // Cluster 3: Boolean-Flags zurueck auf Wizard-Slugs.
    // ACHTUNG: 'ja_austreten' nicht rekonstruierbar — true wird zu 'ja'.
    kirchensteuer: boolToJaNeinSmall((c as Record<string, unknown>).pays_church_tax),
    immobilie: boolToJaNein(c.owns_property),
    krankenkasse: c.health_insurance_provider ?? '',
    // Cluster 4: Wizard-Slugs aus additional_info.
    // Tri-State (24.09.2026): fehlender Wert → leere Auswahl, nicht 'Nein'.
    // Sonst wird "unbekannt" beim naechsten Speichern zu einem gespeicherten
    // false (Rueckweg-Befund zum Pro-User-Flag wizard_pflichtfelder_gelockert).
    arbeitskraftabsicherung: boolToJaNeinOderLeer(c.additional_info?.has_disability_insurance, 'Ja', 'Nein'),
    sparvertraege: boolToJaNeinOderLeer(c.additional_info?.has_savings_contracts, 'ja', 'nein'),
    // Kontakt-Stammdaten — vorher hart leer fuer anrede, jetzt aus salutation.
    vorname: c.first_name ?? '',
    nachname: c.last_name ?? '',
    telefonnummer: c.phone ?? '',
    email: c.email ?? '',
  };
}

/**
 * Mapping fuer den Partner-Teil. Quelle: `customer.partner_data` (JSONB,
 * Decision #69 / Cluster 1). Die `customer.partner`-Relation ist fuer
 * Wizard-Kunden grundsaetzlich null und wird hier nicht gelesen.
 *
 * Felder im JSONB liegen bereits als Wizard-Slugs vor (z.B.
 * `berufsstand=Angestellt`), keine Enum-Umkehr noetig.
 */
function mapPartner(pd: PartnerData): Partial<CallcenterWizardData> {
  return {
    anredePartner: pd.anrede ?? '',
    vornamePartner: pd.vorname ?? '',
    nachnamePartner: pd.nachname ?? '',
    berufsstand2: pd.berufsstand ?? '',
    berufsbezeichnungPartner: pd.berufsbezeichnung ?? '',
    geburtsjahrPartner: pd.geburtsjahr || null,
    bruttogehalt2: toNum(pd.bruttogehalt),
    nettogehalt2: toNum(pd.nettogehalt),
    krankenkassePartner: pd.krankenkasse ?? '',
    einkuenfte_partner: pd.einkuenfte ?? 'keine',
    einkuenfteHoehe_partner: toNum(pd.einkuenfteHoehe),
    // Wizard-Slug ja|nein. JSONB-Wert ist bereits ein Slug.
    kirchensteuerPartner: (pd.kirchensteuer as string | undefined) ?? 'nein',
  };
}

export function buildWizardDataFromCustomer(
  customer: Partial<Customer>,
): Partial<CallcenterWizardData> {
  const mandant = mapMandant(customer);

  const partnerData = customer.partner_data;
  if (partnerData && Object.keys(partnerData).length > 0) {
    return { ...mandant, ...mapPartner(partnerData) };
  }

  return mandant;
}

// Re-export fuer Tests — typecheck der Sparvertrag-Verkettung.
export type { Sparvertrag };
